import GetPath from "@/custolink-logic/api/GetPath";
import {
  AdmSupportVideoCallSyncApi as AdmSupportVideoCallSyncApiHandles,
  PublicSupportVideoCallSyncApi as PublicSupportVideoCallSyncApiHandles,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import type {
  SyncActiveSourceInput,
  SyncFrameDescriptor,
  SyncOpAccepted,
  SyncOpInput,
  SyncRoomStateView,
  SyncSnapshotView,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import {
  AdmSupportVideoCallSyncApi as AdmSupportVideoCallSyncApiGeneric,
  PublicSupportVideoCallSyncApi as PublicSupportVideoCallSyncApiGeneric,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_generic";
import type { Rotation } from "./coordinates/sourceToViewport";

/**
 * syncApi - the one place worker and guest talk to the backend from, behind a single SyncApi
 * shape. RoomSyncProvider (Task 3) calls these functions inside useMutation; nothing here calls
 * useMutation/useQuery itself.
 *
 * Only transpiled handles are used (AdmSupportVideoCallSyncApi / PublicSupportVideoCallSyncApi,
 * generated by `./gradlew runTranspileAll` into custolink-transpiled_by_server/) - never a
 * hand-rolled HTTP call (BASELINE rule, user memory feedback_frontend_api.md).
 */

export type {
  SyncActiveSourceInput,
  SyncFrameDescriptor,
  SyncOpAccepted,
  SyncOpInput,
  SyncRoomStateView,
  SyncSnapshotView,
};

export type FrameUploadInput = {
  frameId: string;
  trackSid: string;
  width: number;
  height: number;
  mirrored: boolean;
  rotation: Rotation;
  capturedAtEpochMs: number;
};

/** Worker-only mutations (D-03, D-07, D-11, D-12): never exposed on the guest side. */
export interface WorkerSyncActions {
  setAnnotationPermission(granted: boolean): Promise<SyncRoomStateView>;
  setActiveSource(src: SyncActiveSourceInput): Promise<SyncRoomStateView>;
  requestFreeze(src: SyncActiveSourceInput): Promise<SyncRoomStateView>;
  resumeLive(): Promise<SyncRoomStateView>;
  saveSnapshot(frameId: string, upToSeq: number, png: File): Promise<SyncSnapshotView>;
}

export interface SyncApi {
  readonly role: "worker" | "customer";
  getRoomState(sinceSeq: number): Promise<SyncRoomStateView>;
  appendOp(input: SyncOpInput): Promise<SyncOpAccepted>;
  uploadFrame(input: FrameUploadInput, file: File): Promise<SyncFrameDescriptor>;
  frameImageUrl(frameId: string): string;
  readonly worker: WorkerSyncActions | null;
}

/** Worker (org-scoped admin) side, backed by AdmSupportVideoCallSyncApi. */
export function workerSyncApi(orgId: number, callId: number): SyncApi {
  const worker: WorkerSyncActions = {
    setAnnotationPermission: (granted) =>
      AdmSupportVideoCallSyncApiHandles.setAnnotationPermission(orgId, callId, granted),
    setActiveSource: (src) => AdmSupportVideoCallSyncApiHandles.setActiveSource(orgId, callId, src),
    requestFreeze: (src) => AdmSupportVideoCallSyncApiHandles.requestFreeze(orgId, callId, src),
    resumeLive: () => AdmSupportVideoCallSyncApiHandles.resumeLive(orgId, callId),
    saveSnapshot: (frameId, upToSeq, png) =>
      AdmSupportVideoCallSyncApiHandles.saveSnapshot(orgId, callId, frameId, upToSeq, png),
  };

  return {
    role: "worker",
    getRoomState: (sinceSeq) => AdmSupportVideoCallSyncApiHandles.getRoomState(orgId, callId, sinceSeq),
    appendOp: (input) => AdmSupportVideoCallSyncApiHandles.appendOp(orgId, callId, input),
    uploadFrame: (input, file) =>
      AdmSupportVideoCallSyncApiHandles.uploadFrame(
        orgId,
        callId,
        input.frameId,
        input.trackSid,
        input.width,
        input.height,
        input.mirrored,
        input.rotation,
        input.capturedAtEpochMs,
        file,
      ),
    frameImageUrl: (frameId) =>
      AdmSupportVideoCallSyncApiGeneric.streamFrameImage(GetPath, orgId, callId, frameId),
    worker,
  };
}

/**
 * Guest (roomId-scoped, public) side, backed by PublicSupportVideoCallSyncApi. Must never
 * reference AdmSupportVideoCallSyncApi - the worker-only surface is unreachable from here.
 */
export function guestSyncApi(roomId: string): SyncApi {
  return {
    role: "customer",
    getRoomState: (sinceSeq) => PublicSupportVideoCallSyncApiHandles.enduserGetRoomState(roomId, sinceSeq),
    appendOp: (input) => PublicSupportVideoCallSyncApiHandles.enduserAppendOp(roomId, input),
    uploadFrame: (input, file) =>
      PublicSupportVideoCallSyncApiHandles.enduserUploadFrame(
        roomId,
        input.frameId,
        input.trackSid,
        input.width,
        input.height,
        input.mirrored,
        input.rotation,
        input.capturedAtEpochMs,
        file,
      ),
    frameImageUrl: (frameId) =>
      PublicSupportVideoCallSyncApiGeneric.enduserStreamFrameImage(GetPath, roomId, frameId),
    worker: null,
  };
}
